/**
 * Conversation limit ("modo cola") — admin configuration validation.
 * Ranges come from spec §2: company limit 1–50 (default 5), per-agent limit
 * 1–1000 (an AI-bot account absorbs what the humans cannot take),
 * idleReleaseMinutes 15–1,440 (default 120), `null` disables idle release.
 */

export const LIMIT_MIN = 1;
export const LIMIT_MAX = 50;
export const AGENT_LIMIT_MAX = 1000;
export const DEFAULT_LIMIT = 5;
export const IDLE_MIN = 15;
export const IDLE_MAX = 1440;
export const DEFAULT_IDLE_MINUTES = 120;

const LIMIT_ERROR = `Debe ser un número entero entre ${LIMIT_MIN} y ${LIMIT_MAX}.`;
const AGENT_LIMIT_ERROR = `Debe ser un número entero entre ${LIMIT_MIN} y ${AGENT_LIMIT_MAX}.`;
const IDLE_ERROR = `Debe ser un número entero de minutos entre ${IDLE_MIN} y ${IDLE_MAX}.`;

/** Strict integer parse: "5" → 5; "", "2.5", "abc", null → NaN. */
const toInt = (value) => {
  if (typeof value === 'number') return Number.isInteger(value) ? value : NaN;
  if (typeof value !== 'string' || !/^-?\d+$/.test(value.trim())) return NaN;
  return Number(value.trim());
};

const inRange = (n, min, max) => Number.isInteger(n) && n >= min && n <= max;

/** Error message, or null when valid. */
export const validateLimit = (value) =>
  inRange(toInt(value), LIMIT_MIN, LIMIT_MAX) ? null : LIMIT_ERROR;

/** Per-agent override: same rule, wider range. */
export const validateAgentLimit = (value) =>
  inRange(toInt(value), LIMIT_MIN, AGENT_LIMIT_MAX) ? null : AGENT_LIMIT_ERROR;

/** `null` = idle release disabled, which is valid. */
export const validateIdleMinutes = (value) => {
  if (value === null) return null;
  return inRange(toInt(value), IDLE_MIN, IDLE_MAX) ? null : IDLE_ERROR;
};

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

const collectAgentLimits = (agentLimits, errors) => {
  const source = agentLimits && typeof agentLimits === 'object' && !Array.isArray(agentLimits) ? agentLimits : {};
  return Object.keys(source).reduce((acc, userId) => {
    if (isBlank(source[userId])) return acc; // empty = use the company default
    const error = validateAgentLimit(source[userId]);
    if (error) errors[`agentLimits.${userId}`] = error;
    else acc[userId] = toInt(source[userId]);
    return acc;
  }, {});
};

/**
 * Turns the form state into the PUT /conversation-limit/config body.
 * @returns {{ ok: boolean, payload: object|null, errors: Object<string,string> }}
 */
export const buildConfigPayload = (form) => {
  const source = form && typeof form === 'object' && !Array.isArray(form) ? form : {};
  const errors = {};

  const limitError = validateLimit(source.defaultLimit);
  if (limitError) errors.defaultLimit = limitError;

  const idleValue = source.idleEnabled === true ? source.idleReleaseMinutes : null;
  const idleError = source.idleEnabled === true ? validateIdleMinutes(isBlank(idleValue) ? NaN : idleValue) : null;
  if (idleError) errors.idleReleaseMinutes = idleError;

  const agentLimits = collectAgentLimits(source.agentLimits, errors);

  if (Object.keys(errors).length > 0) return { ok: false, payload: null, errors };
  return {
    ok: true,
    errors,
    payload: {
      defaultLimit: toInt(source.defaultLimit),
      agentLimits,
      idleReleaseMinutes: idleValue === null ? null : toInt(idleValue),
    },
  };
};
